import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { RealtimeProvider } from "@/contexts/RealtimeContext";
import { SystemConfigProvider, useSystemConfig } from "@/contexts/SystemConfigContext";

// Helper to automatically reload the page on dynamic import failures (e.g. after a new deployment)
const lazyRetry = <T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
) => {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (error) {
      console.error("Failed to load dynamically imported module. Reloading page...", error);
      window.location.reload();
      return new Promise<{ default: T }>(() => {}); // Keeps Suspense in loading state while page reloads
    }
  });
};

// Lazy load route components for code splitting
const Index = lazyRetry(() => import("./pages/Index"));
const Auth = lazyRetry(() => import("./pages/Auth"));
const Install = lazyRetry(() => import("./pages/Install"));
const ImportKlines = lazyRetry(() => import("./pages/ImportKlines"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));

// Lazy load docs pages
const DocsLayout = lazyRetry(() => import("./pages/docs/DocsLayout"));
const DocsIndex = lazyRetry(() => import("./pages/docs/DocsIndex"));

// Minimal loading fallback to prevent layout shift
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      gcTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});
export const VERSION = "v57.0-DEFIBRILLATOR-ARMED";
const SystemVersionLabel = () => {
  const { execContext } = useSystemConfig();
  return (
    <div className="fixed bottom-0 left-0 w-full bg-blue-600/20 text-blue-500 text-[8px] font-mono text-right pr-2 py-0.5 z-[9999] pointer-events-none select-none">
      CODE_SYNC_{VERSION}_TRACE
    </div>
  );
};

const App = () => {
  // Initialize push notifications
  usePushNotifications();
  return <QueryClientProvider client={queryClient}>
    <RealtimeProvider>
      <SystemConfigProvider>
        <SystemVersionLabel />
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/install" element={<Install />} />
                <Route path="/import-klines" element={<ImportKlines />} />
                <Route path="docs" element={<DocsLayout />}>
                  <Route index element={<DocsIndex />} />
                </Route>
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </SystemConfigProvider>
    </RealtimeProvider>
  </QueryClientProvider>;
};
export default App;

